import { api } from "@/lib/api";
import type { ApiResponse, Course, PaginatedResponse } from "@learn-me/types";

interface DashboardStats {
  users: { total: number; newThisMonth: number };
  courses: { total: number; published: number };
  enrollments: { total: number; active: number; completed: number };
  revenue: { total: number; thisMonth: number };
  recentActivity: {
    enrollments: Array<{
      id: string;
      enrolledAt: string;
      user: { id: string; name: string; email: string };
      course: { id: string; title: string; slug: string };
    }>;
    payments: Array<{
      id: string;
      amount: number;
      paidAt: string;
      user: { id: string; name: string; email: string };
      course: { id: string; title: string };
    }>;
  };
}

interface SalesAnalytics {
  period: { start: string; end: string };
  dailySales: Array<{ date: string; total: number; count: number }>;
  topCourses: Array<{
    course: { id: string; title: string; slug: string };
    revenue: number;
    sales: number;
  }>;
}

interface StudentListItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
  createdAt: string;
  enrollmentsCount: number;
  purchasesCount: number;
  enrollments: Array<{
    progress: number;
    course: { title: string };
  }>;
}

export interface StudentDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
  createdAt: string;
  totalSpent: number;
  enrollments: Array<{
    id: string;
    progress: number;
    enrolledAt: string;
    lastAccessedAt?: string;
    course: {
      id: string;
      title: string;
      slug: string;
      thumbnailUrl?: string;
    };
  }>;
  payments: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    completedAt?: string;
    course: {
      id: string;
      title: string;
    };
  }>;
  quizAttempts: Array<{
    id: string;
    score: number;
    status: string;
    startedAt: string;
    completedAt?: string;
    quiz: {
      title: string;
      chapter: {
        title: string;
        course: {
          title: string;
        };
      };
    };
  }>;
}

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  createdAt: string;
  paidAt?: string;
  user: { id: string; name: string; email: string };
  course: { id: string; title: string; slug: string };
}

// Temp file for AI generation
export interface TempFile {
  url: string;
  publicId: string;
  type: "PDF" | "IMAGE";
  name: string;
  mimeType: string;
  size: number;
  extractedText?: string;
}

// Custom chapter from wizard
export interface WizardChapter {
  title: string;
  description?: string;
  estimatedDuration: number;
  includeQuiz: boolean;
}

// AI Generation types
export interface AIGenerationParams {
  topic: string;
  duration: "SHORT" | "MEDIUM" | "LONG";
  level: "BEGINNER" | "INTERMEDIATE" | "ADVANCED";
  language: "es" | "en";
  includeQuizzes: boolean;
  additionalNotes?: string;
  tempFiles?: TempFile[];
  // Custom chapters from wizard (optional - if not provided, uses DURATION_CONFIG)
  chapters?: WizardChapter[];
}

// Reference document types
export interface ReferenceDoc {
  id: string;
  courseId: string;
  name: string;
  url: string;
  publicId: string;
  type: "PDF" | "IMAGE";
  size: number;
  mimeType: string;
  extractedText: string | null;
  isProcessed: boolean;
  createdAt: string;
}

// Chapter AI generation types
export interface GeneratedChapterContent {
  content: string;
  keyPoints: string[];
}

export interface GeneratedQuiz {
  title: string;
  questions: Array<{
    text: string;
    explanation: string;
    options: Array<{
      text: string;
      isCorrect: boolean;
    }>;
  }>;
}

// TTS types (Azure Speech Spanish voices)
export interface TTSVoice {
  id: string;
  name: string;
  description: string;
  locale: string;
  gender: "female" | "male";
}

export interface GeneratedAudio {
  audioUrl: string;
  audioDuration: number;
  audioVoice: string;
}

// Mermaid diagram types
export interface MermaidDiagram {
  type: string;
  code: string;
  description: string;
  insertAfterSection?: string;
}

// Section Diagram types (stored diagrams)
export interface SectionDiagram {
  id: string;
  sectionId: string;
  type: string;
  title: string;
  mermaidCode: string;
  description: string | null;
  position: number;
  status: "GENERATING" | "COMPLETED" | "FAILED" | "NEEDS_REVIEW" | "PENDING";
  needsReview: boolean;
  reviewNote: string | null;
  generationError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChapterDiagramsResponse {
  sectionId: string;
  sectionTitle: string;
  diagrams: SectionDiagram[];
}

export interface DiagramVersion {
  id: string;
  diagramId: string;
  versionNumber: number;
  mermaidCode: string;
  changeNote: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface UpdateDiagramInput {
  mermaidCode?: string;
  title?: string;
  description?: string;
  position?: number;
  needsReview?: boolean;
  reviewNote?: string;
}

// Chapter Section types
export interface ChapterSection {
  id: string;
  chapterId: string;
  title: string;
  content: string;
  position: number;
  audioUrl: string | null;
  audioPublicId: string | null;
  audioDuration: number | null;
  audioVoice: string | null;
  audioGeneratedAt: string | null;
  // Featured image (slideshow)
  featuredImageUrl: string | null;
  featuredImageBlobId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSectionInput {
  title: string;
  content: string;
  position?: number;
}

export interface UpdateSectionInput {
  title?: string;
  content?: string;
}

// Finance Analytics types
export interface FinanceAnalytics {
  period: { start: string; end: string };
  kpis: {
    totalRevenue: number;
    revenueThisMonth: number;
    totalSales: number;
    salesThisMonth: number;
    refundCount: number;
    refundRate: number;
    averageOrderValue: number;
    comparison?: {
      revenueChange: number;
      salesChange: number;
      aovChange: number;
    };
  };
  monthlySales: Array<{
    month: string;
    revenue: number;
    count: number;
  }>;
  currencyDistribution: Array<{
    currency: string;
    total: number;
    count: number;
    percentage: number;
  }>;
  topCourses: Array<{
    course: { id: string; title: string; slug: string };
    revenue: number;
    sales: number;
    percentage: number;
  }>;
  statusDistribution: Array<{
    status: string;
    count: number;
    amount: number;
  }>;
}

export interface Transaction {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  course: {
    id: string;
    title: string;
    slug: string;
  };
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
}

export interface TransactionsResponse {
  payments: Transaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Invitation types
export interface Invitation {
  id: string;
  email: string;
  token: string;
  status: "PENDING" | "ACCEPTED" | "EXPIRED" | "CANCELLED";
  firstName: string | null;
  lastName: string | null;
  autoEnrollCourseId: string | null;
  autoEnrollBundleId: string | null;
  autoEnrollCourse: { id: string; title: string } | null;
  autoEnrollBundle: { id: string; name: string } | null;
  invitedById: string;
  expiresAt: string;
  acceptedAt: string | null;
  user: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvitationsResponse {
  invitations: Invitation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface InvitationStats {
  total: number;
  pending: number;
  accepted: number;
  expired: number;
  cancelled: number;
}

// Student Management types
export interface StudentListItemEnhanced {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
  isActive: boolean;
  createdAt: string;
  enrollmentsCount: number;
  purchasesCount: number;
  enrollments: Array<{
    progress: number;
    course: { title: string };
  }>;
}

export interface StudentsEnhancedResponse {
  students: StudentListItemEnhanced[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface EnrollmentEnhanced {
  id: string;
  status: string;
  progress: number;
  enrolledAt: string;
  expiresAt: string | null;
  completedAt: string | null;
  lastAccessedAt: string | null;
  enrolledById: string | null;
  enrollmentNote: string | null;
  source: string;
  course: {
    id: string;
    title: string;
    slug: string;
    thumbnailUrl: string | null;
  };
}

export interface BundleEnrollmentEnhanced {
  id: string;
  status: string;
  enrolledAt: string;
  expiresAt: string | null;
  enrolledById: string | null;
  enrollmentNote: string | null;
  bundle: {
    id: string;
    name: string;
    slug: string;
    thumbnailUrl: string | null;
  };
}

export interface StudentDetailEnhanced {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
  isActive: boolean;
  deactivatedAt: string | null;
  deactivatedReason: string | null;
  createdAt: string;
  totalSpent: number;
  enrollments: EnrollmentEnhanced[];
  bundleEnrollments: BundleEnrollmentEnhanced[];
  payments: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    completedAt: string | null;
    course: { id: string; title: string };
  }>;
  quizAttempts: Array<{
    id: string;
    score: number | null;
    status: string;
    startedAt: string;
    completedAt: string | null;
    quiz: {
      title: string;
      chapter: {
        title: string;
        course: { title: string };
      };
    };
  }>;
  badges: Array<{
    id: string;
    earnedAt: string;
    badge: {
      name: string;
      description: string;
      icon: string | null;
      course: { title: string };
    };
  }>;
}

// AI Task types
export interface AITask {
  id: string;
  type: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED" | "CANCELLED";
  courseId: string | null;
  chapterId: string | null;
  sectionId: string | null;
  quizId: string | null;
  progress: number;
  currentStep: string | null;
  processedItems: number | null;
  totalItems: number | null;
  result: unknown;
  errorMessage: string | null;
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  course?: { id: string; title: string };
  chapter?: { id: string; title: string };
}

export interface AITaskAction {
  type: string;
  label: string;
  description: string;
  enabled: boolean;
}

export interface AITaskStatus {
  courseId: string;
  courseTitle: string;
  isAIGenerated: boolean;
  stats: {
    totalChapters: number;
    totalSections: number;
    sectionsWithDiagrams: number;
    sectionsWithAudio: number;
    diagramCoverage: number;
    audioCoverage: number;
  };
  recentTasks: AITask[];
  availableActions: AITaskAction[];
}

// Content Validation types
export interface ContentIssue {
  type: "COMPETITOR" | "PROHIBITED" | "QUALITY" | "BRAND";
  severity: "ERROR" | "WARNING";
  message: string;
  location?: string;
  suggestion?: string;
}

export interface SectionValidationResult {
  sectionId: string;
  sectionTitle: string;
  issues: ContentIssue[];
  hasErrors: boolean;
  hasWarnings: boolean;
}

export interface ChapterValidationResult {
  chapterId: string;
  chapterTitle: string;
  sections: SectionValidationResult[];
  totalIssues: number;
  totalErrors: number;
  totalWarnings: number;
}

export interface CourseValidationResult {
  courseId: string;
  courseTitle: string;
  chapters: Array<{
    chapterId: string;
    chapterTitle: string;
    issueCount: number;
    errorCount: number;
  }>;
  totalIssues: number;
  totalErrors: number;
  summary: string;
}

export interface CleanContentResult {
  chapterId: string;
  sectionsProcessed: number;
  totalChangesMade: number;
  changes: Array<{
    sectionId: string;
    sectionTitle: string;
    changesMade: string[];
  }>;
}

export interface CleanSectionResult {
  sectionId: string;
  changesMade: string[];
  originalContent: string;
  cleanedContent: string;
}

export interface CourseWithIssues {
  courseId: string;
  courseTitle: string;
  issueCount: number;
  errorCount: number;
}

export interface CompetitorMention {
  courseId: string;
  courseTitle: string;
  chapterId: string;
  chapterTitle: string;
  sectionId: string;
  sectionTitle: string;
  issue: ContentIssue;
}

// Course Preview types
export interface CoursePreview {
  id: string;
  title: string;
  slug: string;
  description: string;
  shortDescription: string | null;
  thumbnailUrl: string | null;
  previewVideoUrl: string | null;
  isFree: boolean;
  price: number | null;
  currency: string;
  level: string | null;
  duration: number | null;
  status: string;
  categories: Array<{ id: string; name: string; slug: string }>;
  chapters: Array<{
    id: string;
    title: string;
    description: string | null;
    position: number;
    content: string | null;
    isPublished: boolean;
    audioUrl: string | null;
    audioDuration: number | null;
    sections: Array<{
      id: string;
      title: string;
      content: string;
      position: number;
      audioUrl: string | null;
      audioDuration: number | null;
    }>;
  }>;
}

// Generated chapter with sections from AI job
export interface GeneratedChapterFromJob {
  title: string;
  description: string;
  estimatedDuration: number;
  sections: Array<{ title: string; content: string }>;
  quiz?: {
    title: string;
    questions: Array<{
      text: string;
      explanation: string;
      options: Array<{ text: string; isCorrect: boolean }>;
    }>;
  };
}

export interface AIGenerationJob {
  id: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
  topic: string;
  duration: string;
  level: string;
  language: string;
  currentStep: string | null;
  totalSteps: number;
  completedSteps: number;
  progress: number;
  errorMessage: string | null;
  progressMessage: string | null;
  // Chapters with sections and quizzes (populated when job is COMPLETED)
  chapters: GeneratedChapterFromJob[];
  // Raw output from AI generation (for preview in AIGenerationModal)
  rawOutput?: {
    title: string;
    description: string;
    chapters: Array<{ title: string; position: number; quiz?: unknown }>;
    gamification?: { badges?: unknown[]; activities?: unknown[] };
    _meta?: {
      canResume: boolean;
      lastCompletedChapterIndex: number;
      lastCompletedStep: number;
    };
  };
  generatedCourse: { id: string; title: string; slug: string } | null;
  createdAt: string;
  completedAt: string | null;
  // Indica si el job puede ser resumido después de un fallo
  canResume?: boolean;
}

export const adminApi = {
  async getDashboard(): Promise<DashboardStats> {
    const response =
      await api.get<ApiResponse<DashboardStats>>("/admin/dashboard");
    return response.data.data!;
  },

  async getSalesAnalytics(
    startDate?: string,
    endDate?: string,
  ): Promise<SalesAnalytics> {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    const response = await api.get<ApiResponse<SalesAnalytics>>(
      `/admin/analytics/sales?${params}`,
    );
    return response.data.data!;
  },

  async getStudents(
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<PaginatedResponse<StudentListItem>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.set("search", search);
    const response = await api.get<
      ApiResponse<PaginatedResponse<StudentListItem>>
    >(`/admin/students?${params}`);
    return response.data.data!;
  },

  async getStudentDetail(studentId: string): Promise<StudentDetail> {
    const response = await api.get<ApiResponse<StudentDetail>>(
      `/admin/students/${studentId}`,
    );
    return response.data.data!;
  },

  async getPayments(
    page = 1,
    limit = 20,
    status?: string,
  ): Promise<PaginatedResponse<Payment>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status) params.set("status", status);
    const response = await api.get<
      ApiResponse<{
        payments: Payment[];
        pagination: PaginatedResponse<Payment>["pagination"];
      }>
    >(`/admin/payments?${params}`);
    const data = response.data.data!;
    // Normalize response to match PaginatedResponse type
    return {
      items: data.payments,
      pagination: data.pagination,
    };
  },

  async getCourses(
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<PaginatedResponse<Course>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.set("search", search);
    const response = await api.get<ApiResponse<PaginatedResponse<Course>>>(
      `/admin/courses?${params}`,
    );
    return response.data.data!;
  },

  async getCourse(id: string): Promise<
    Course & {
      chapters?: Array<{
        id: string;
        title: string;
        position: number;
        isPublished: boolean;
      }>;
    }
  > {
    const response = await api.get<
      ApiResponse<
        Course & {
          chapters?: Array<{
            id: string;
            title: string;
            position: number;
            isPublished: boolean;
          }>;
        }
      >
    >(`/admin/courses/${id}`);
    return response.data.data!;
  },

  async createCourse(data: Partial<Course>): Promise<Course> {
    const response = await api.post<ApiResponse<Course>>("/courses", data);
    return response.data.data!;
  },

  async updateCourse(id: string, data: Partial<Course>): Promise<Course> {
    const response = await api.put<ApiResponse<Course>>(`/courses/${id}`, data);
    return response.data.data!;
  },

  async deleteCourse(id: string): Promise<void> {
    await api.delete(`/courses/${id}`);
  },

  // Chapter CRUD
  async createChapter(data: {
    courseId: string;
    title: string;
    description?: string;
    content?: string;
    position?: number;
    isFree?: boolean;
    isPublished?: boolean;
  }): Promise<{
    id: string;
    title: string;
    position: number;
    isPublished: boolean;
  }> {
    const response = await api.post<
      ApiResponse<{
        id: string;
        title: string;
        position: number;
        isPublished: boolean;
      }>
    >("/chapters", data);
    return response.data.data!;
  },

  // Quiz CRUD
  async createQuiz(data: {
    chapterId: string;
    title: string;
    description?: string;
    passingScore?: number;
    questions: Array<{
      text: string;
      explanation?: string;
      options: Array<{
        text: string;
        isCorrect: boolean;
      }>;
    }>;
  }): Promise<{ id: string; title: string }> {
    const response = await api.post<ApiResponse<{ id: string; title: string }>>(
      "/quizzes",
      data,
    );
    return response.data.data!;
  },

  async archiveCourse(id: string): Promise<Course> {
    const response = await api.post<ApiResponse<Course>>(
      `/admin/courses/${id}/archive`,
    );
    return response.data.data!;
  },

  async unarchiveCourse(id: string): Promise<Course> {
    const response = await api.post<ApiResponse<Course>>(
      `/admin/courses/${id}/unarchive`,
    );
    return response.data.data!;
  },

  async validateCourseForPublishing(id: string): Promise<{
    canPublish: boolean;
    issues: Array<{
      code: string;
      message: string;
      severity: "error" | "warning";
    }>;
  }> {
    const response = await api.get<
      ApiResponse<{
        canPublish: boolean;
        issues: Array<{
          code: string;
          message: string;
          severity: "error" | "warning";
        }>;
      }>
    >(`/admin/courses/${id}/validate-publish`);
    return response.data.data!;
  },

  async duplicateCourse(id: string): Promise<{
    id: string;
    title: string;
    slug: string;
    status: string;
  }> {
    const response = await api.post<
      ApiResponse<{
        id: string;
        title: string;
        slug: string;
        status: string;
      }>
    >(`/admin/courses/${id}/duplicate`);
    return response.data.data!;
  },

  async reorderChapters(
    courseId: string,
    chapters: Array<{ id: string; position: number }>,
  ): Promise<
    Array<{ id: string; title: string; position: number; isPublished: boolean }>
  > {
    const response = await api.put<
      ApiResponse<
        Array<{
          id: string;
          title: string;
          position: number;
          isPublished: boolean;
        }>
      >
    >(`/chapters/course/${courseId}/reorder`, { chapters });
    return response.data.data!;
  },

  async deleteChapter(chapterId: string): Promise<void> {
    await api.delete(`/chapters/${chapterId}`);
  },

  async refundPayment(paymentId: string): Promise<void> {
    await api.post(`/admin/payments/${paymentId}/refund`);
  },

  // AI Course Generation
  async generateCourse(
    params: AIGenerationParams,
  ): Promise<{ jobId: string; status: string; message: string }> {
    const response = await api.post<
      ApiResponse<{ jobId: string; status: string; message: string }>
    >("/admin/ai/generate", params);
    return response.data.data!;
  },

  // Generate outline only (for wizard)
  async generateOutline(params: {
    topic: string;
    duration: "SHORT" | "MEDIUM" | "LONG";
    level: "BEGINNER" | "INTERMEDIATE" | "ADVANCED";
    language?: "es" | "en";
    additionalNotes?: string;
    referenceContext?: string;
  }): Promise<{
    title: string;
    description: string;
    shortDescription: string;
    estimatedDuration: number;
    chapters: Array<{
      position: number;
      title: string;
      description: string;
      estimatedDuration: number;
      isFree: boolean;
    }>;
  }> {
    const response = await api.post<
      ApiResponse<{
        title: string;
        description: string;
        shortDescription: string;
        estimatedDuration: number;
        chapters: Array<{
          position: number;
          title: string;
          description: string;
          estimatedDuration: number;
          isFree: boolean;
        }>;
      }>
    >("/admin/ai/generate-outline", params);
    return response.data.data!;
  },

  async getGenerationJob(jobId: string): Promise<AIGenerationJob> {
    const response = await api.get<ApiResponse<AIGenerationJob>>(
      `/admin/ai/jobs/${jobId}`,
    );
    return response.data.data!;
  },

  async confirmGeneration(
    jobId: string,
  ): Promise<{ id: string; title: string; slug: string; status: string }> {
    const response = await api.post<
      ApiResponse<{ id: string; title: string; slug: string; status: string }>
    >(`/admin/ai/jobs/${jobId}/confirm`);
    return response.data.data!;
  },

  async deleteGenerationJob(jobId: string): Promise<void> {
    await api.delete(`/admin/ai/jobs/${jobId}`);
  },

  async resumeGenerationJob(jobId: string): Promise<AIGenerationJob> {
    const response = await api.post<ApiResponse<AIGenerationJob>>(
      `/admin/ai/jobs/${jobId}/resume`,
    );
    return response.data.data!;
  },

  async retryChapter(
    jobId: string,
    chapterIndex: number,
  ): Promise<{
    id: string;
    status: string;
    message: string;
    chapter: { index: number; title: string; sections: number };
    allChaptersComplete: boolean;
  }> {
    const response = await api.post<
      ApiResponse<{
        id: string;
        status: string;
        message: string;
        chapter: { index: number; title: string; sections: number };
        allChaptersComplete: boolean;
      }>
    >(`/admin/ai/jobs/${jobId}/chapter/${chapterIndex}/retry`);
    return response.data.data!;
  },

  // File Upload
  async uploadFile(file: File): Promise<TempFile> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<ApiResponse<TempFile>>(
      "/admin/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data.data!;
  },

  async deleteUpload(publicId: string): Promise<void> {
    await api.delete(`/admin/upload/${encodeURIComponent(publicId)}`);
  },

  // Reference Documents
  async getCourseReferenceDocs(courseId: string): Promise<ReferenceDoc[]> {
    const response = await api.get<ApiResponse<ReferenceDoc[]>>(
      `/admin/courses/${courseId}/reference-docs`,
    );
    return response.data.data!;
  },

  async addCourseReferenceDoc(
    courseId: string,
    file: File,
  ): Promise<ReferenceDoc> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<ApiResponse<ReferenceDoc>>(
      `/admin/courses/${courseId}/reference-docs`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data.data!;
  },

  async deleteCourseReferenceDoc(
    courseId: string,
    docId: string,
  ): Promise<void> {
    await api.delete(`/admin/courses/${courseId}/reference-docs/${docId}`);
  },

  async reprocessReferenceDoc(
    courseId: string,
    docId: string,
  ): Promise<ReferenceDoc> {
    const response = await api.post<ApiResponse<ReferenceDoc>>(
      `/admin/courses/${courseId}/reference-docs/${docId}/reprocess`,
    );
    return response.data.data!;
  },

  // Chapter AI Generation
  async generateChapterContent(
    chapterId: string,
    useReferenceDocs = true,
  ): Promise<GeneratedChapterContent> {
    const response = await api.post<ApiResponse<GeneratedChapterContent>>(
      `/admin/ai/chapters/${chapterId}/generate-content`,
      { useReferenceDocs },
    );
    return response.data.data!;
  },

  async generateChapterSections(
    chapterId: string,
    useReferenceDocs = true,
  ): Promise<{
    sections: Array<{ title: string; content: string }>;
    keyPoints: string[];
  }> {
    const response = await api.post<
      ApiResponse<{
        sections: Array<{ title: string; content: string }>;
        keyPoints: string[];
      }>
    >(`/admin/ai/chapters/${chapterId}/generate-sections`, {
      useReferenceDocs,
    });
    return response.data.data!;
  },

  async generateChapterQuiz(
    chapterId: string,
    useReferenceDocs = true,
  ): Promise<GeneratedQuiz> {
    const response = await api.post<ApiResponse<GeneratedQuiz>>(
      `/admin/ai/chapters/${chapterId}/generate-quiz`,
      { useReferenceDocs },
    );
    return response.data.data!;
  },

  async improveChapterContent(
    chapterId: string,
    useReferenceDocs = true,
  ): Promise<{ content: string; improvements: string[] }> {
    const response = await api.post<
      ApiResponse<{ content: string; improvements: string[] }>
    >(`/admin/ai/chapters/${chapterId}/improve-content`, { useReferenceDocs });
    return response.data.data!;
  },

  async extendChapterContent(
    chapterId: string,
    options: { useReferenceDocs?: boolean; focusArea?: string } = {},
  ): Promise<{ content: string; addedSections: string[] }> {
    const response = await api.post<
      ApiResponse<{ content: string; addedSections: string[] }>
    >(`/admin/ai/chapters/${chapterId}/extend-content`, options);
    return response.data.data!;
  },

  // Quiz AI Improvement
  async improveQuiz(
    quizId: string,
    options: {
      useReferenceDocs?: boolean;
      focus?: string;
      applyImmediately?: boolean;
    } = {},
  ): Promise<{
    improvements: string[];
    quiz: GeneratedQuiz;
    applied: boolean;
  }> {
    const response = await api.post<
      ApiResponse<{
        improvements: string[];
        quiz: GeneratedQuiz;
        applied: boolean;
      }>
    >(`/admin/ai/quizzes/${quizId}/improve`, options);
    return response.data.data!;
  },

  async applyQuizImprovements(
    quizId: string,
    quiz: GeneratedQuiz,
  ): Promise<void> {
    await api.post(`/admin/ai/quizzes/${quizId}/apply-improvements`, { quiz });
  },

  // Course AI Generation
  async generateAdditionalChapters(
    courseId: string,
    options: { count: number; topic?: string; useReferenceDocs?: boolean },
  ): Promise<{
    chapters: Array<{
      title: string;
      description: string;
      sections: Array<{ title: string; content: string }>;
    }>;
  }> {
    const response = await api.post<
      ApiResponse<{
        chapters: Array<{
          title: string;
          description: string;
          sections: Array<{ title: string; content: string }>;
        }>;
      }>
    >(`/admin/ai/courses/${courseId}/generate-chapters`, options);
    return response.data.data!;
  },

  async improveCourseDescription(
    courseId: string,
  ): Promise<{ description: string; shortDescription: string }> {
    const response = await api.post<
      ApiResponse<{ description: string; shortDescription: string }>
    >(`/admin/ai/courses/${courseId}/improve-description`);
    return response.data.data!;
  },

  // TTS (Text-to-Speech)
  async getTTSVoices(): Promise<TTSVoice[]> {
    const response =
      await api.get<ApiResponse<TTSVoice[]>>("/admin/tts/voices");
    return response.data.data!;
  },

  async generateChapterAudio(
    chapterId: string,
    options: { voice?: string; model?: string } = {},
  ): Promise<GeneratedAudio> {
    const response = await api.post<ApiResponse<GeneratedAudio>>(
      `/admin/chapters/${chapterId}/generate-audio`,
      options,
    );
    return response.data.data!;
  },

  async deleteChapterAudio(chapterId: string): Promise<void> {
    await api.delete(`/admin/chapters/${chapterId}/audio`);
  },

  // Diagram Generation
  async generateChapterDiagrams(
    chapterId: string,
    options: { diagramType?: string } = {},
  ): Promise<{ diagrams: MermaidDiagram[] }> {
    const response = await api.post<
      ApiResponse<{ diagrams: MermaidDiagram[] }>
    >(`/admin/ai/chapters/${chapterId}/generate-diagrams`, options);
    return response.data.data!;
  },

  // ============================================
  // Diagram Management
  // ============================================

  async getChapterDiagrams(
    chapterId: string,
  ): Promise<ChapterDiagramsResponse[]> {
    const response = await api.get<ApiResponse<ChapterDiagramsResponse[]>>(
      `/diagrams/chapter/${chapterId}`,
    );
    return response.data.data || [];
  },

  async getDiagram(diagramId: string): Promise<SectionDiagram> {
    const response = await api.get<ApiResponse<SectionDiagram>>(
      `/diagrams/${diagramId}`,
    );
    return response.data.data!;
  },

  async updateDiagram(
    diagramId: string,
    data: UpdateDiagramInput,
  ): Promise<SectionDiagram> {
    const response = await api.patch<ApiResponse<SectionDiagram>>(
      `/diagrams/${diagramId}`,
      data,
    );
    return response.data.data!;
  },

  async deleteDiagram(diagramId: string): Promise<void> {
    await api.delete(`/diagrams/${diagramId}`);
  },

  async regenerateDiagram(diagramId: string): Promise<SectionDiagram> {
    const response = await api.post<ApiResponse<SectionDiagram>>(
      `/diagrams/${diagramId}/regenerate`,
    );
    return response.data.data!;
  },

  async generateSectionDiagram(
    sectionId: string,
    diagramType?:
      | "FLOWCHART"
      | "SEQUENCE"
      | "MINDMAP"
      | "CLASS"
      | "STATE"
      | "PIE"
      | "TIMELINE",
    title?: string,
  ): Promise<SectionDiagram> {
    const response = await api.post<ApiResponse<SectionDiagram>>(
      `/diagrams/generate`,
      { sectionId, diagramType, title },
    );
    return response.data.data!;
  },

  async approveDiagram(diagramId: string): Promise<SectionDiagram> {
    const response = await api.post<ApiResponse<SectionDiagram>>(
      `/diagrams/${diagramId}/approve`,
    );
    return response.data.data!;
  },

  // Diagram Versions
  async getDiagramVersions(diagramId: string): Promise<DiagramVersion[]> {
    const response = await api.get<ApiResponse<DiagramVersion[]>>(
      `/diagrams/${diagramId}/versions`,
    );
    return response.data.data || [];
  },

  async restoreDiagramVersion(
    diagramId: string,
    versionId: string,
  ): Promise<SectionDiagram> {
    const response = await api.post<ApiResponse<SectionDiagram>>(
      `/diagrams/${diagramId}/restore/${versionId}`,
    );
    return response.data.data!;
  },

  // ============================================
  // Chapter Sections
  // ============================================

  async getChapterSections(chapterId: string): Promise<ChapterSection[]> {
    const response = await api.get<ApiResponse<ChapterSection[]>>(
      `/admin/chapters/${chapterId}/sections`,
    );
    return response.data.data!;
  },

  async getSection(sectionId: string): Promise<ChapterSection> {
    const response = await api.get<ApiResponse<ChapterSection>>(
      `/admin/sections/${sectionId}`,
    );
    return response.data.data!;
  },

  async createSection(
    chapterId: string,
    data: CreateSectionInput,
  ): Promise<ChapterSection> {
    const response = await api.post<ApiResponse<ChapterSection>>(
      `/admin/chapters/${chapterId}/sections`,
      data,
    );
    return response.data.data!;
  },

  async generateSectionWithAI(
    chapterId: string,
    options: { topic?: string } = {},
  ): Promise<{ title: string; content: string }> {
    const response = await api.post<
      ApiResponse<{ title: string; content: string }>
    >(`/admin/ai/chapters/${chapterId}/generate-section`, options);
    return response.data.data!;
  },

  async parseContentToSections(chapterId: string): Promise<ChapterSection[]> {
    const response = await api.post<ApiResponse<ChapterSection[]>>(
      `/admin/chapters/${chapterId}/sections/parse`,
    );
    return response.data.data!;
  },

  async updateSection(
    sectionId: string,
    data: UpdateSectionInput,
  ): Promise<ChapterSection> {
    const response = await api.put<ApiResponse<ChapterSection>>(
      `/admin/sections/${sectionId}`,
      data,
    );
    return response.data.data!;
  },

  async deleteSection(sectionId: string): Promise<void> {
    await api.delete(`/admin/sections/${sectionId}`);
  },

  async moveSection(
    sectionId: string,
    targetChapterId: string,
    position?: number,
  ): Promise<ChapterSection> {
    const response = await api.post<ApiResponse<ChapterSection>>(
      `/admin/sections/${sectionId}/move`,
      { targetChapterId, position },
    );
    return response.data.data!;
  },

  async reorderSections(
    chapterId: string,
    sectionIds: string[],
  ): Promise<ChapterSection[]> {
    const response = await api.put<ApiResponse<ChapterSection[]>>(
      `/admin/chapters/${chapterId}/sections/reorder`,
      { sectionIds },
    );
    return response.data.data!;
  },

  async syncSectionsToChapter(chapterId: string): Promise<void> {
    await api.post(`/admin/chapters/${chapterId}/sections/sync`);
  },

  // Section Audio (TTS)
  async generateSectionAudio(
    sectionId: string,
    options: { voice?: string; model?: string } = {},
  ): Promise<GeneratedAudio> {
    const response = await api.post<ApiResponse<GeneratedAudio>>(
      `/admin/sections/${sectionId}/generate-audio`,
      options,
    );
    return response.data.data!;
  },

  async deleteSectionAudio(sectionId: string): Promise<void> {
    await api.delete(`/admin/sections/${sectionId}/audio`);
  },

  // Section Regeneration (AI)
  // Note: Backend returns raw task object (not wrapped in ApiResponse)
  async regenerateSection(
    sectionId: string,
    config: {
      style?: "concise" | "detailed" | "conversational";
      level?: "simpler" | "same" | "advanced";
      focus?: string;
      preserveKeyPoints?: boolean;
      autoApply?: boolean;
    } = {},
  ): Promise<{
    taskId: string;
    status: string;
  }> {
    const response = await api.post<{
      id: string;
      status: string;
    }>(`/ai-tasks/section/${sectionId}/regenerate`, config);
    // Backend returns `id`, map to `taskId` for frontend consistency
    return { taskId: response.data.id, status: response.data.status };
  },

  // Note: Backend returns raw task object (not wrapped in ApiResponse)
  async getAITaskResult(taskId: string): Promise<{
    id: string;
    status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
    result?: {
      sectionId: string;
      applied: boolean;
      comparison: {
        original: { title: string; content: string };
        regenerated: { title: string; content: string; differences: string[] };
        differences: string[];
      };
    };
    error?: string;
  }> {
    const response = await api.get<{
      id: string;
      status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
      result?: {
        sectionId: string;
        applied: boolean;
        comparison: {
          original: { title: string; content: string };
          regenerated: {
            title: string;
            content: string;
            differences: string[];
          };
          differences: string[];
        };
      };
      error?: string;
    }>(`/ai-tasks/${taskId}`);
    return response.data;
  },

  // Apply regenerated content uses updateSection - just a convenience alias
  async applyRegeneratedSection(
    sectionId: string,
    regenerated: { title: string; content: string },
  ): Promise<ChapterSection> {
    // Use the existing updateSection method
    return this.updateSection(sectionId, {
      title: regenerated.title,
      content: regenerated.content,
    });
  },

  async generateAllSectionAudios(
    chapterId: string,
    options: { voice?: string; model?: string } = {},
  ): Promise<GeneratedAudio[]> {
    const response = await api.post<ApiResponse<GeneratedAudio[]>>(
      `/admin/chapters/${chapterId}/generate-all-audio`,
      options,
    );
    return response.data.data!;
  },

  // Course Preview
  async getCoursePreview(courseId: string): Promise<CoursePreview> {
    const response = await api.get<ApiResponse<CoursePreview>>(
      `/admin/courses/${courseId}/preview`,
    );
    return response.data.data!;
  },

  // Course Categories
  async getCourseCategories(
    courseId: string,
  ): Promise<
    Array<{ id: string; name: string; slug: string; isMainArea: boolean }>
  > {
    const response = await api.get<
      ApiResponse<
        Array<{ id: string; name: string; slug: string; isMainArea: boolean }>
      >
    >(`/admin/courses/${courseId}/categories`);
    return response.data.data!;
  },

  async updateCourseCategories(
    courseId: string,
    categoryIds: string[],
  ): Promise<
    Array<{ id: string; name: string; slug: string; isMainArea: boolean }>
  > {
    const response = await api.put<
      ApiResponse<
        Array<{ id: string; name: string; slug: string; isMainArea: boolean }>
      >
    >(`/admin/courses/${courseId}/categories`, { categoryIds });
    return response.data.data!;
  },

  // ============================================
  // Finance Analytics
  // ============================================

  async getFinanceAnalytics(filters?: {
    startDate?: string;
    endDate?: string;
    currency?: "USD" | "CRC";
    compareWithPrevious?: boolean;
  }): Promise<FinanceAnalytics> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.set("startDate", filters.startDate);
    if (filters?.endDate) params.set("endDate", filters.endDate);
    if (filters?.currency) params.set("currency", filters.currency);
    if (filters?.compareWithPrevious) params.set("compareWithPrevious", "true");
    const response = await api.get<ApiResponse<FinanceAnalytics>>(
      `/admin/analytics/finance?${params}`,
    );
    return response.data.data!;
  },

  async getFinanceTransactions(
    page = 1,
    limit = 20,
    filters?: {
      search?: string;
      status?: string;
      currency?: string;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<TransactionsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.currency) params.set("currency", filters.currency);
    if (filters?.startDate) params.set("startDate", filters.startDate);
    if (filters?.endDate) params.set("endDate", filters.endDate);
    const response = await api.get<ApiResponse<TransactionsResponse>>(
      `/admin/analytics/finance/transactions?${params}`,
    );
    return response.data.data!;
  },

  async exportFinanceData(
    type: "summary" | "transactions",
    filters?: {
      startDate?: string;
      endDate?: string;
      currency?: string;
    },
  ): Promise<Blob> {
    const params = new URLSearchParams({ type });
    if (filters?.startDate) params.set("startDate", filters.startDate);
    if (filters?.endDate) params.set("endDate", filters.endDate);
    if (filters?.currency) params.set("currency", filters.currency);
    const response = await api.get(
      `/admin/analytics/finance/export?${params}`,
      { responseType: "blob" },
    );
    return response.data;
  },

  // ============================================
  // Invitations
  // ============================================

  async createInvitation(data: {
    email: string;
    firstName?: string;
    lastName?: string;
    autoEnrollCourseId?: string;
    autoEnrollBundleId?: string;
  }): Promise<Invitation> {
    const response = await api.post<ApiResponse<Invitation>>(
      "/admin/invitations",
      data,
    );
    return response.data.data!;
  },

  async getInvitations(
    page = 1,
    limit = 20,
    filters?: {
      status?: "PENDING" | "ACCEPTED" | "EXPIRED" | "CANCELLED";
      search?: string;
    },
  ): Promise<InvitationsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters?.status) params.set("status", filters.status);
    if (filters?.search) params.set("search", filters.search);
    const response = await api.get<ApiResponse<InvitationsResponse>>(
      `/admin/invitations?${params}`,
    );
    return response.data.data!;
  },

  async getInvitationStats(): Promise<InvitationStats> {
    const response = await api.get<ApiResponse<InvitationStats>>(
      "/admin/invitations/stats",
    );
    return response.data.data!;
  },

  async resendInvitation(id: string): Promise<Invitation> {
    const response = await api.post<ApiResponse<Invitation>>(
      `/admin/invitations/${id}/resend`,
    );
    return response.data.data!;
  },

  async cancelInvitation(id: string): Promise<void> {
    await api.delete(`/admin/invitations/${id}`);
  },

  // ============================================
  // Student Management
  // ============================================

  async getStudentsEnhanced(
    page = 1,
    limit = 20,
    filters?: {
      search?: string;
      status?: "all" | "active" | "inactive";
    },
  ): Promise<StudentsEnhancedResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);
    const response = await api.get<ApiResponse<StudentsEnhancedResponse>>(
      `/admin/students/enhanced?${params}`,
    );
    return response.data.data!;
  },

  async getStudentDetailEnhanced(
    studentId: string,
  ): Promise<StudentDetailEnhanced> {
    const response = await api.get<ApiResponse<StudentDetailEnhanced>>(
      `/admin/students/${studentId}/enhanced`,
    );
    return response.data.data!;
  },

  async updateStudent(
    studentId: string,
    data: { firstName?: string; lastName?: string; avatarUrl?: string },
  ): Promise<StudentListItemEnhanced> {
    const response = await api.put<ApiResponse<StudentListItemEnhanced>>(
      `/admin/students/${studentId}`,
      data,
    );
    return response.data.data!;
  },

  async activateStudent(studentId: string): Promise<{ message: string }> {
    const response = await api.post<ApiResponse<{ message: string }>>(
      `/admin/students/${studentId}/activate`,
    );
    return response.data.data!;
  },

  async deactivateStudent(
    studentId: string,
    reason?: string,
  ): Promise<{ message: string }> {
    const response = await api.post<ApiResponse<{ message: string }>>(
      `/admin/students/${studentId}/deactivate`,
      { reason },
    );
    return response.data.data!;
  },

  async enrollStudent(
    studentId: string,
    data: {
      courseId?: string;
      bundleId?: string;
      expiresAt?: string;
      note?: string;
    },
  ): Promise<{ message: string }> {
    const response = await api.post<ApiResponse<{ message: string }>>(
      `/admin/students/${studentId}/enroll`,
      data,
    );
    return response.data.data!;
  },

  async cancelEnrollment(enrollmentId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `/admin/enrollments/${enrollmentId}`,
    );
    return response.data.data!;
  },

  async cancelBundleEnrollment(
    bundleEnrollmentId: string,
  ): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `/admin/bundle-enrollments/${bundleEnrollmentId}`,
    );
    return response.data.data!;
  },

  // ============================================
  // AI Tasks
  // ============================================

  async getCourseAIStatus(courseId: string): Promise<AITaskStatus> {
    const response = await api.get<ApiResponse<AITaskStatus>>(
      `/ai-tasks/course/${courseId}/status`,
    );
    return response.data.data!;
  },

  async getCourseTasks(courseId: string, limit = 20): Promise<AITask[]> {
    const response = await api.get<ApiResponse<AITask[]>>(
      `/ai-tasks/course/${courseId}/history?limit=${limit}`,
    );
    return response.data.data!;
  },

  async createAITask(data: {
    type: string;
    courseId?: string;
    chapterId?: string;
    sectionId?: string;
    quizId?: string;
  }): Promise<AITask> {
    const response = await api.post<ApiResponse<AITask>>("/ai-tasks", data);
    return response.data.data!;
  },

  async getAITask(taskId: string): Promise<AITask> {
    const response = await api.get<ApiResponse<AITask>>(`/ai-tasks/${taskId}`);
    return response.data.data!;
  },

  async cancelAITask(taskId: string): Promise<AITask> {
    const response = await api.post<ApiResponse<AITask>>(
      `/ai-tasks/${taskId}/cancel`,
    );
    return response.data.data!;
  },

  async generateCourseDiagrams(courseId: string): Promise<AITask> {
    const response = await api.post<ApiResponse<AITask>>(
      `/ai-tasks/course/${courseId}/diagrams`,
    );
    return response.data.data!;
  },

  async validateCourseContent(courseId: string): Promise<AITask> {
    const response = await api.post<ApiResponse<AITask>>(
      `/ai-tasks/course/${courseId}/validate`,
    );
    return response.data.data!;
  },

  async cleanCourseContent(courseId: string): Promise<AITask> {
    const response = await api.post<ApiResponse<AITask>>(
      `/ai-tasks/course/${courseId}/clean`,
    );
    return response.data.data!;
  },

  async analyzeCourseQuality(courseId: string): Promise<AITask> {
    const response = await api.post<ApiResponse<AITask>>(
      `/ai-tasks/course/${courseId}/analyze`,
    );
    return response.data.data!;
  },

  async getActiveTasks(limit = 20): Promise<AITask[]> {
    const response = await api.get<ApiResponse<AITask[]>>(
      `/ai-tasks/status/active?limit=${limit}`,
    );
    return response.data.data!;
  },

  async getMyTasks(limit = 50): Promise<AITask[]> {
    const response = await api.get<ApiResponse<AITask[]>>(
      `/ai-tasks/user/my-tasks?limit=${limit}`,
    );
    return response.data.data!;
  },

  // ============================================
  // Content Scanning (synchronous validation)
  // ============================================

  async scanCourseContent(courseId: string): Promise<CourseValidationResult> {
    const response = await api.get<ApiResponse<CourseValidationResult>>(
      `/content-validation/course/${courseId}`,
    );
    return response.data.data!;
  },

  async scanChapterContent(
    chapterId: string,
  ): Promise<ChapterValidationResult> {
    const response = await api.get<ApiResponse<ChapterValidationResult>>(
      `/content-validation/chapter/${chapterId}`,
    );
    return response.data.data!;
  },

  async autoCleanChapterContent(
    chapterId: string,
  ): Promise<CleanContentResult> {
    const response = await api.post<ApiResponse<CleanContentResult>>(
      `/content-validation/chapter/${chapterId}/clean`,
    );
    return response.data.data!;
  },

  async autoCleanSectionContent(
    sectionId: string,
  ): Promise<CleanSectionResult> {
    const response = await api.post<ApiResponse<CleanSectionResult>>(
      `/content-validation/section/${sectionId}/clean`,
    );
    return response.data.data!;
  },

  async getCoursesWithContentIssues(limit = 10): Promise<CourseWithIssues[]> {
    const response = await api.get<ApiResponse<CourseWithIssues[]>>(
      `/content-validation/issues?limit=${limit}`,
    );
    return response.data.data!;
  },

  async findCompetitorMentions(limit = 50): Promise<CompetitorMention[]> {
    const response = await api.get<ApiResponse<CompetitorMention[]>>(
      `/content-validation/competitor-mentions?limit=${limit}`,
    );
    return response.data.data!;
  },

  // ===========================================
  // Section Image Upload
  // ===========================================

  async uploadSectionImage(
    sectionId: string,
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<{ url: string; blobName: string; size: number }> {
    const formData = new FormData();
    formData.append("image", file);

    const response = await api.post<
      ApiResponse<{
        url: string;
        blobName: string;
        contentType: string;
        size: number;
      }>
    >(`/admin/sections/${sectionId}/images`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    });
    return response.data.data!;
  },

  // ===========================================
  // Featured Image (Slideshow)
  // ===========================================

  async uploadSectionFeaturedImage(
    sectionId: string,
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<{ url: string; blobId: string }> {
    const formData = new FormData();
    formData.append("image", file);

    const response = await api.put<
      ApiResponse<{
        url: string;
        blobId: string;
      }>
    >(`/admin/sections/${sectionId}/featured-image`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    });
    return response.data.data!;
  },

  async deleteSectionFeaturedImage(sectionId: string): Promise<void> {
    await api.delete(`/admin/sections/${sectionId}/featured-image`);
  },

  // ============================================
  // Modules
  // ============================================

  async getModulesByCourse(courseId: string): Promise<{
    modules: Array<{
      id: string;
      title: string;
      description: string | null;
      position: number;
      chapters: Array<{
        id: string;
        title: string;
        position: number;
      }>;
    }>;
    unassignedChapters: Array<{
      id: string;
      title: string;
      position: number;
    }>;
  }> {
    const response = await api.get<
      ApiResponse<{
        modules: Array<{
          id: string;
          title: string;
          description: string | null;
          position: number;
          chapters: Array<{
            id: string;
            title: string;
            position: number;
          }>;
        }>;
        unassignedChapters: Array<{
          id: string;
          title: string;
          position: number;
        }>;
      }>
    >(`/modules/course/${courseId}`);
    return response.data.data!;
  },

  async createModule(data: {
    courseId: string;
    title: string;
    description?: string;
  }): Promise<{
    id: string;
    title: string;
    description: string | null;
    position: number;
  }> {
    const response = await api.post<
      ApiResponse<{
        id: string;
        title: string;
        description: string | null;
        position: number;
      }>
    >("/modules", data);
    return response.data.data!;
  },

  async updateModule(
    moduleId: string,
    data: {
      title?: string;
      description?: string | null;
    },
  ): Promise<{
    id: string;
    title: string;
    description: string | null;
    position: number;
  }> {
    const response = await api.put<
      ApiResponse<{
        id: string;
        title: string;
        description: string | null;
        position: number;
      }>
    >(`/modules/${moduleId}`, data);
    return response.data.data!;
  },

  async deleteModule(moduleId: string): Promise<void> {
    await api.delete(`/modules/${moduleId}`);
  },

  async reorderModules(courseId: string, moduleIds: string[]): Promise<void> {
    await api.put(`/modules/course/${courseId}/reorder`, { moduleIds });
  },

  async assignChaptersToModule(
    moduleId: string,
    chapterIds: string[],
  ): Promise<void> {
    await api.put(`/modules/${moduleId}/chapters`, { chapterIds });
  },

  async removeChapterFromModule(
    moduleId: string,
    chapterId: string,
  ): Promise<void> {
    await api.delete(`/modules/${moduleId}/chapters/${chapterId}`);
  },

  // ============================================
  // Final Exams
  // ============================================

  async getFinalExamByCourse(courseId: string): Promise<{
    id: string;
    courseId: string;
    title: string;
    description: string | null;
    passingScore: number;
    maxAttempts: number;
    timeLimit: number | null;
    shuffleQuestions: boolean;
    shuffleOptions: boolean;
    requiredForCertificate: boolean;
    conceptualWeight: number;
    applicationWeight: number;
    integrativeCaseWeight: number;
    conceptualQuestionsPerAttempt: number | null;
    applicationQuestionsPerAttempt: number | null;
    integrativeCaseQuestionsPerAttempt: number | null;
    questions: Array<{
      id: string;
      text: string;
      explanation: string | null;
      points: number;
      position: number;
      section: "CONCEPTUAL" | "APPLICATION" | "INTEGRATIVE_CASE";
      difficulty: "EASY" | "MEDIUM" | "HARD";
      tags: string[];
      options: Array<{
        id: string;
        text: string;
        isCorrect: boolean;
        position: number;
      }>;
    }>;
    _count: { attempts: number };
  } | null> {
    const response = await api.get(`/final-exams/admin/course/${courseId}`);
    return response.data.data ?? null;
  },

  async createFinalExam(data: {
    courseId: string;
    title: string;
    description?: string;
    passingScore?: number;
    maxAttempts?: number;
    timeLimit?: number | null;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    requiredForCertificate?: boolean;
    conceptualWeight?: number;
    applicationWeight?: number;
    integrativeCaseWeight?: number;
    conceptualQuestionsPerAttempt?: number | null;
    applicationQuestionsPerAttempt?: number | null;
    integrativeCaseQuestionsPerAttempt?: number | null;
    questions: Array<{
      text: string;
      explanation?: string;
      points?: number;
      section: "CONCEPTUAL" | "APPLICATION" | "INTEGRATIVE_CASE";
      difficulty?: "EASY" | "MEDIUM" | "HARD";
      tags?: string[];
      options: Array<{ text: string; isCorrect: boolean }>;
    }>;
  }) {
    const response = await api.post("/final-exams/admin", data);
    return response.data.data!;
  },

  async updateFinalExam(
    finalExamId: string,
    data: {
      title?: string;
      description?: string | null;
      passingScore?: number;
      maxAttempts?: number;
      timeLimit?: number | null;
      shuffleQuestions?: boolean;
      shuffleOptions?: boolean;
      requiredForCertificate?: boolean;
      conceptualWeight?: number;
      applicationWeight?: number;
      integrativeCaseWeight?: number;
      conceptualQuestionsPerAttempt?: number | null;
      applicationQuestionsPerAttempt?: number | null;
      integrativeCaseQuestionsPerAttempt?: number | null;
    },
  ) {
    const response = await api.put(`/final-exams/admin/${finalExamId}`, data);
    return response.data.data!;
  },

  async deleteFinalExam(finalExamId: string): Promise<void> {
    await api.delete(`/final-exams/admin/${finalExamId}`);
  },

  // ============================================
  // Enterprise Contacts
  // ============================================

  async getEnterpriseContacts(
    page = 1,
    limit = 20,
    filters?: { status?: string; search?: string },
  ): Promise<{
    requests: Array<{
      id: string;
      companyName: string;
      contactName: string;
      email: string;
      phone: string | null;
      employeeCount: string | null;
      employeeCountLabel: string | null;
      offering: { slug: string; title: string; category: string } | null;
      message: string;
      status: string;
      notesCount: number;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (filters?.status) params.set("status", filters.status);
    if (filters?.search) params.set("search", filters.search);
    const response = await api.get<
      ApiResponse<{
        requests: Array<{
          id: string;
          companyName: string;
          contactName: string;
          email: string;
          phone: string | null;
          employeeCount: string | null;
          employeeCountLabel: string | null;
          offering: { slug: string; title: string; category: string } | null;
          message: string;
          status: string;
          notesCount: number;
          createdAt: string;
          updatedAt: string;
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>
    >(`/enterprise/admin/contacts?${params}`);
    return response.data.data!;
  },

  async getEnterpriseContactStats(): Promise<{
    total: number;
    byStatus: {
      pending: number;
      contacted: number;
      converted: number;
      closed: number;
    };
    conversionRate: number;
  }> {
    const response = await api.get<
      ApiResponse<{
        total: number;
        byStatus: {
          pending: number;
          contacted: number;
          converted: number;
          closed: number;
        };
        conversionRate: number;
      }>
    >("/enterprise/admin/contacts/stats");
    return response.data.data!;
  },

  async getEnterpriseContact(requestId: string): Promise<{
    id: string;
    companyName: string;
    contactName: string;
    email: string;
    phone: string | null;
    employeeCount: string | null;
    employeeCountLabel: string | null;
    offering: { slug: string; title: string; category: string } | null;
    message: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    notes: Array<{
      id: string;
      content: string;
      createdAt: string;
      admin: { firstName: string; lastName: string };
    }>;
  }> {
    const response = await api.get<
      ApiResponse<{
        id: string;
        companyName: string;
        contactName: string;
        email: string;
        phone: string | null;
        employeeCount: string | null;
        employeeCountLabel: string | null;
        offering: { slug: string; title: string; category: string } | null;
        message: string;
        status: string;
        createdAt: string;
        updatedAt: string;
        notes: Array<{
          id: string;
          content: string;
          createdAt: string;
          admin: { firstName: string; lastName: string };
        }>;
      }>
    >(`/enterprise/admin/contacts/${requestId}`);
    return response.data.data!;
  },

  async updateEnterpriseContactStatus(
    requestId: string,
    status: "PENDING" | "CONTACTED" | "CONVERTED" | "CLOSED",
  ): Promise<void> {
    await api.put(`/enterprise/admin/contacts/${requestId}/status`, { status });
  },

  async addEnterpriseContactNote(
    requestId: string,
    content: string,
  ): Promise<{
    id: string;
    content: string;
    createdAt: string;
    admin: { firstName: string; lastName: string };
  }> {
    const response = await api.post<
      ApiResponse<{
        id: string;
        content: string;
        createdAt: string;
        admin: { firstName: string; lastName: string };
      }>
    >(`/enterprise/admin/contacts/${requestId}/notes`, { content });
    return response.data.data!;
  },

  async deleteEnterpriseContact(requestId: string): Promise<void> {
    await api.delete(`/enterprise/admin/contacts/${requestId}`);
  },

  // ============================================
  // Team Members
  // ============================================

  async getTeamMembers(includeInactive = false): Promise<
    Array<{
      id: string;
      name: string;
      role: string;
      title: string;
      photoUrl: string | null;
      bio: string;
      linkedInUrl: string | null;
      signatureUrl: string | null;
      position: number;
      isActive: boolean;
      createdAt: string;
    }>
  > {
    const endpoint = includeInactive ? "/team/admin/all" : "/team";
    const response = await api.get<
      ApiResponse<
        Array<{
          id: string;
          name: string;
          role: string;
          title: string;
          photoUrl: string | null;
          bio: string;
          linkedInUrl: string | null;
          signatureUrl: string | null;
          position: number;
          isActive: boolean;
          createdAt: string;
        }>
      >
    >(endpoint);
    return response.data.data!;
  },

  async getTeamMember(memberId: string): Promise<{
    id: string;
    name: string;
    role: string;
    title: string;
    photoUrl: string | null;
    bio: string;
    linkedInUrl: string | null;
    signatureUrl: string | null;
    position: number;
    isActive: boolean;
    createdAt: string;
  }> {
    const response = await api.get<
      ApiResponse<{
        id: string;
        name: string;
        role: string;
        title: string;
        photoUrl: string | null;
        bio: string;
        linkedInUrl: string | null;
        signatureUrl: string | null;
        position: number;
        isActive: boolean;
        createdAt: string;
      }>
    >(`/team/admin/${memberId}`);
    return response.data.data!;
  },

  async createTeamMember(data: {
    name: string;
    role: "ACADEMIC_DIRECTOR" | "EXECUTIVE_DIRECTOR" | "INSTRUCTOR" | "OTHER";
    title: string;
    bio: string;
    photoUrl?: string;
    linkedInUrl?: string;
    signatureUrl?: string;
    isActive?: boolean;
  }): Promise<{
    id: string;
    name: string;
    role: string;
    title: string;
  }> {
    const response = await api.post<
      ApiResponse<{
        id: string;
        name: string;
        role: string;
        title: string;
      }>
    >("/team/admin", data);
    return response.data.data!;
  },

  async updateTeamMember(
    memberId: string,
    data: {
      name?: string;
      role?:
        | "ACADEMIC_DIRECTOR"
        | "EXECUTIVE_DIRECTOR"
        | "INSTRUCTOR"
        | "OTHER";
      title?: string;
      bio?: string;
      photoUrl?: string | null;
      linkedInUrl?: string | null;
      signatureUrl?: string | null;
      isActive?: boolean;
    },
  ): Promise<{
    id: string;
    name: string;
    role: string;
    title: string;
  }> {
    const response = await api.put<
      ApiResponse<{
        id: string;
        name: string;
        role: string;
        title: string;
      }>
    >(`/team/admin/${memberId}`, data);
    return response.data.data!;
  },

  async deleteTeamMember(memberId: string): Promise<void> {
    await api.delete(`/team/admin/${memberId}`);
  },

  async reorderTeamMembers(memberIds: string[]): Promise<void> {
    await api.put("/team/admin/reorder", { memberIds });
  },

  async toggleTeamMemberActive(memberId: string): Promise<{
    id: string;
    isActive: boolean;
  }> {
    const response = await api.put<
      ApiResponse<{
        id: string;
        isActive: boolean;
      }>
    >(`/team/admin/${memberId}/toggle-active`);
    return response.data.data!;
  },
};
